import React from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { BookOpen, Code2, Compass, History, List, Menu, Monitor, Moon, Search, Sun } from "lucide-react";
import type { SiteData } from "../shared/types";
import { siteUrl } from "../shared/paths";
import { useDocsTheme, Theme } from "./theme";
import { COPY } from "../copy";

/**
 * The header row (design §0.2): logo · Search (⌘K) · Contact sales · Sign up · theme menu, and the
 * top tabs below it (§0.1). A link with no address decided (`DEVELOPER_LINKS` null, `shared/links.ts`) is not shown.
 */

export const TAB_ICONS: Record<string, React.ReactNode> = {
  book: <BookOpen size={16} />,
  compass: <Compass size={16} />,
  list: <List size={16} />,
  code: <Code2 size={16} />,
  history: <History size={16} />,
};

const THEMES: { value: Theme; label: string; icon: React.ReactNode }[] = [
  { value: "system", label: COPY.themeSystem, icon: <Monitor size={14} /> },
  { value: "light", label: COPY.themeLight, icon: <Sun size={14} /> },
  { value: "dark", label: COPY.themeDark, icon: <Moon size={14} /> },
];

/** The theme choice (system / light / dark, default system; `theme.tsx`). */
export const ThemeMenu = () => {
  const { theme, setTheme } = useDocsTheme();

  return (
    <div className="flex items-center rounded-full border border-border p-0.5" role="radiogroup" aria-label={COPY.theme}>
      {THEMES.map((t) => (
        <button
          key={t.value}
          type="button"
          role="radio"
          aria-checked={theme === t.value}
          aria-label={t.label}
          onClick={() => setTheme(t.value)}
          className={clsx("rounded-full p-1.5", theme === t.value ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground")}
        >
          {t.icon}
        </button>
      ))}
    </div>
  );
};

export const Header = ({ site, activeTab, onSearch, onMenu }: { site: SiteData; activeTab?: string; onSearch: () => void; onMenu: () => void }) => (
  <header className="sticky top-0 z-30 border-b border-border bg-background">
    <div className="flex h-14 items-center gap-3 px-4 clg:px-6">
      <button type="button" onClick={onMenu} className="text-muted-foreground clg:hidden" aria-label={COPY.navigation}>
        <Menu size={20} />
      </button>
      <Link to={siteUrl("/")} className="text-head6 font-semibold text-foreground">
        {COPY.siteName}
      </Link>
      <button type="button" onClick={onSearch} className="ml-2 flex flex-1 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-bodySmall text-muted-foreground clg:max-w-sm">
        <Search size={14} />
        <span className="flex-1 text-left">{COPY.search}</span>
        <kbd className="hidden text-headline2 clg:inline">{COPY.searchShortcut}</kbd>
      </button>
      <div className="ml-auto hidden items-center gap-4 text-bodySmall clg:flex">
        {site.links.contactSales ? (
          <a href={site.links.contactSales} className="text-muted-foreground hover:text-foreground">
            {COPY.contactSales}
          </a>
        ) : null}
        <a href={site.links.signUp} className="rounded-full bg-primary px-3 py-1.5 font-semibold text-primary-foreground">
          {COPY.signUp}
        </a>
        <ThemeMenu />
      </div>
    </div>
    <nav className="flex gap-1 overflow-x-auto px-4 clg:px-6" aria-label={COPY.navigation}>
      {site.tabs.map((tab) => (
        <Link
          key={tab.id}
          to={siteUrl(tab.path)}
          className={clsx(
            "flex shrink-0 items-center gap-2 border-b-2 px-3 py-2 text-bodySmall",
            tab.id === activeTab ? "border-purpleGreen text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          {TAB_ICONS[tab.icon]}
          {tab.label}
        </Link>
      ))}
    </nav>
  </header>
);
